import { create } from 'zustand';
import {
  personasApiV2,
  type PersonaClient,
  type PersonaDetail,
} from '@/services/api';

interface PersonasState {
  clients: PersonaClient[];
  selectedClientId: string | null;
  personas: PersonaDetail[];
  selectedPersona: PersonaDetail | null;
  enriched: boolean;
  loading: boolean;
  error: string | null;

  fetchClients: () => Promise<void>;
  selectClient: (clientId: string) => Promise<void>;
  selectPersona: (persona: PersonaDetail | null) => void;
  toggleEnriched: () => void;
}

export const usePersonasStore = create<PersonasState>((set, get) => ({
  clients: [],
  selectedClientId: null,
  personas: [],
  selectedPersona: null,
  enriched: false,
  loading: false,
  error: null,

  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      const { clients } = await personasApiV2.listClients();
      set({ clients });
      if (!get().selectedClientId && clients.length > 0) {
        get().selectClient(clients[0].client_id);
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load clients' });
    } finally {
      set({ loading: false });
    }
  },

  selectClient: async (clientId: string) => {
    set({ selectedClientId: clientId, loading: true, error: null, selectedPersona: null });
    try {
      const fetcher = get().enriched
        ? personasApiV2.listEnrichedPersonas
        : personasApiV2.listPersonas;
      const { personas } = await fetcher(clientId);
      set({ personas });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load personas' });
    } finally {
      set({ loading: false });
    }
  },

  selectPersona: (persona) => set({ selectedPersona: persona }),

  toggleEnriched: () => {
    const next = !get().enriched;
    set({ enriched: next });
    const clientId = get().selectedClientId;
    if (clientId) get().selectClient(clientId);
  },
}));
